/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Sheet, SheetContent, SheetClose, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Pill,
  X,
  Store,
  ClipboardList,
  User,
  CheckCircle,
  Phone,
  Mail,
  Calendar,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { TooltipProvider } from '@/components/ui/tooltip';
import RxPadForm, { type RxPadFormRef } from './RxPadForm';
import { medicationUnits } from './RxPadForm/constants';
import { patientService } from '@/services/patientService';
import { useAuth } from '@/contexts/AuthContext';
import { prescriptionService } from '@/services/prescriptionService';
import { Patient, Practitioner } from '@/lib/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClinic } from '@/contexts/ClinicContext';
import { useOptionalEncounter } from '@/contexts/EncounterContext';
import { allergiesService } from '@/services/allergiesService';
import { getDefaultEmailAndPhone } from '@/lib/utils';
import { smartService } from '@/services/smart.service';
import { getSession } from '@/lib/smartSession';
import { getAllPrograms, type Program } from '@/services/programs.service';
import IntelliHealthRxBanner from '@/components/intellihealth/IntelliHealthRxBanner';
import { getProducts as getClinicProducts, getProductsForPatient } from '@/services/products.service';
import {
  getPrograms,
  getProgramDetails,
  getProgramDefaultMedications,
  type Program as StorefrontProgram,
  type DefaultMedication,
} from '@/services/storefrontService';
import { clinicProgramService, type PrescriptionProduct } from '@/services/clinicProgramService';
import { useGuidedOrder } from '@/hooks/useGuidedOrders';
import ConfirmDialog from '@/components/ui/confirm-dialog';
import PrescriptionReviewScreen, { type ReviewMedication } from '../PrescriptionReviewScreen';
import MedicationDrawer from '@/components/shared/MedicationDrawer';
import { MedicationSelection as SharedMedicationSelection } from '@/components/shared/medication-types';
import FollowupLabStep from './FollowupLabStep';
import AllergyWarningDialog, { type AllergyWarning } from './AllergyWarningDialog';
import SyringeWarningDialog from './SyringeWarningDialog';
import { checkSyringeNeeded } from './syringeCheck';
import { checkAllergyInteractions } from '@/services/products.service';

// Import split components
import PatientSelection from './PatientSelection';
import ProgramsList from './ProgramsList';
import ProgramDetails from './ProgramDetails';
import PrescriptionFooter from './PrescriptionFooter';

// Import types
import { PrescriptionFormSheetProps, PrescriptionFormData, MedicationSelection } from './types';
import { formatToLocalDate, toTitleCase } from '@/lib/utils';
import { formatDOB } from '@/lib/dateUtils';
import { queryKeys } from '@/lib/queryClient';

const PrescriptionFormSheet: React.FC<PrescriptionFormSheetProps> = ({
  open,
  onOpenChange,
  onSubmit,
  initialData,
  mode,
  patientId,
  practitionerId,
  prefillMedications,
  guidedOrderId,
  encounterId: encounterIdProp,
  appointmentId: appointmentIdProp,
}) => {
  const { user } = useAuth();
  const { isPractitioner, isGuided, clinic } = useClinic();
  // Encounter linkage: explicit prop wins, else inherit from the active
  // visit cockpit context (if any). Out-of-visit Rx flows leave both blank
  // and the service supplies its `direct_rx` off_encounter_reason default.
  const encounterCtx = useOptionalEncounter();
  const encounterId = encounterIdProp || encounterCtx?.encounterId;
  const appointmentId = appointmentIdProp || encounterCtx?.appointmentId || undefined;
  // Allergy warnings now read from FHIR AllergyIntolerance, not the
  // legacy `patient.medication_allergies` array.
  const [structuredAllergyDisplays, setStructuredAllergyDisplays] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const rxPadRef = useRef<RxPadFormRef>(null);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  // Refresh structured allergies whenever the selected patient changes.
  useEffect(() => {
    let cancelled = false;
    if (!clinic?.id || !selectedPatient?.id) {
      setStructuredAllergyDisplays([]);
      return;
    }
    (async () => {
      try {
        const names = await allergiesService.getActiveMedicationAllergyNames(clinic.id, selectedPatient.id);
        if (!cancelled) setStructuredAllergyDisplays(names);
      } catch (err) {
        // Non-fatal — fall back to legacy array if present.
        if (!cancelled) {
          setStructuredAllergyDisplays(
            (selectedPatient as any)?.medication_allergies as string[] || []
          );
        }
      }
    })();
    return () => { cancelled = true; };
  }, [clinic?.id, selectedPatient?.id]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [formData, setFormData] = useState<PrescriptionFormData>({
    patient_id: patientId || '',
    practitioner_identifier: practitionerId || '',
    medication_requests: [
      {
        medication: '',
        dose: '',
        quantity_authorized: '',
        refills_authorized: '',
        unit_of_measure: '',
        sig: '',
        date_issued: new Date().toISOString().split('T')[0],
        flavor: '',
        note: '',
        reason_for_compounding: '',
        do_not_fill: false,
        do_not_refill: false,
        refill_as_needed: false,
        patient_contact_requested: false,
        product_id: null,
      },
    ],
  });

  const [selectedPractitioner, setSelectedPractitioner] = useState<Practitioner | null>(null);

  const [errors, setErrors] = useState<Record<string, string>>({});

  // External patients (cross-clinic access via external_patients) can only
  // receive Rx via the RX Pad. Storefront products belong to the patient's
  // home clinic, not the current prescribing clinic, so the Store Front tab
  // is hidden for them.
  const isExternalPatient = !!(
    selectedPatient?.clinic_id
    && clinic?.id
    && selectedPatient.clinic_id !== clinic.id
  );

  // Storefront tab state
  const [activeTab, setActiveTab] = useState<'storefront' | 'rxpad'>(mode === 'edit' || prefillMedications ? 'rxpad' : 'storefront');

  // Force RX Pad tab when prefillMedications is provided, when the
  // patient is from a different clinic (external access), or when we're
  // prescribing against a specific Nimbus guided order.
  useEffect(() => {
    if ((prefillMedications || isExternalPatient || guidedOrderId) && open) {
      setActiveTab('rxpad');
    }
  }, [prefillMedications, open, isExternalPatient, guidedOrderId]);

  const [storefrontPrograms, setStorefrontPrograms] = useState<StorefrontProgram[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<any | null>(null);
  const [selectedMedications, setSelectedMedications] = useState<
    Record<string, MedicationSelection>
  >({});
  const [defaultMedicationIds, setDefaultMedicationIds] = useState<Set<string>>(new Set());
  const [selectedProtocolOptionId, setSelectedProtocolOptionId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [loadingProgramDetails, setLoadingProgramDetails] = useState(false);
  const [productCompoundingReasons, setProductCompoundingReasons] = useState<Record<string, string>>({});

  // Tab switch confirmation dialog state
  const [showTabSwitchConfirm, setShowTabSwitchConfirm] = useState(false);
  const [pendingTabValue, setPendingTabValue] = useState<'storefront' | 'rxpad' | null>(null);

  useEffect(() => {
    const fetchProductCompoundingReasons = async () => {
      if (!clinic?.id) return;
      try {
        const response = await getClinicProducts(clinic.id, {
          page: 1,
          limit: 10000,
          sortBy: 'created_at',
          sortOrder: 'DESC',
          search: '',
        });
        const reasonMap = (response.products || []).reduce(
          (acc: Record<string, string>, product: any) => {
            if (product?.active_reason_for_compounding_context) {
              acc[product.id] = product.active_reason_for_compounding_context;
              if (product.identifier) {
                acc[product.identifier] = product.active_reason_for_compounding_context;
              }
            }
            return acc;
          },
          {}
        );
        setProductCompoundingReasons(reasonMap);
      } catch (error) {
        console.error('Failed to fetch compounding reasons', error);
      }
    };

    fetchProductCompoundingReasons();
  }, [clinic?.id]);

  useEffect(() => {
    const fetchPatientSpecificProducts = async () => {
      if (!clinic?.id || !selectedPatient?.id) return
      try {
        const response = await getProductsForPatient(clinic.id, selectedPatient.id, {
          page: 1,
          limit: 10000,
          sortBy: 'created_at',
          sortOrder: 'DESC',
          search: '',
        })
        const reasonMap = (response.products || []).reduce(
          (acc: Record<string, string>, product: any) => {
            if (product?.active_reason_for_compounding_context) {
              acc[product.id] = product.active_reason_for_compounding_context
              if (product.identifier) {
                acc[product.identifier] = product.active_reason_for_compounding_context
              }
            }
            return acc
          },
          {}
        )
        setProductCompoundingReasons(reasonMap)
      } catch (error) {
        console.error('Failed to fetch patient-specific products', error)
      }
    }

    fetchPatientSpecificProducts()
  }, [clinic?.id, selectedPatient?.id])

  // Priority level state (shared across both tabs)
  const [priorityLevel, setPriorityLevel] = useState<number>(1);
  const [treatmentType, setTreatmentType] = useState<string>('');

  // Review screen state
  const [showReview, setShowReview] = useState(false);
  const [reviewMedications, setReviewMedications] = useState<ReviewMedication[]>([]);
  const [priceWarningAcknowledged, setPriceWarningAcknowledged] = useState(false);
  const [rxPadMedicationsData, setRxPadMedicationsData] = useState<any[]>([]);

  // Follow-up Lab state
  const [showFollowupLab, setShowFollowupLab] = useState(false);
  const [followupDays, setFollowupDays] = useState<string>('');
  const [orderFollowupLab, setOrderFollowupLab] = useState(false);
  const [selectedJunctionTest, setSelectedJunctionTest] = useState<any>(null);

  // Check if lab panel selection is enabled for this clinic.
  // When prescribing against a Nimbus guided order, the follow-up labs step
  // is skipped — labs are governed by the guided-order flow, not the Rx form.
  const labPanelSelectionEnabled =
    clinic?.lab_panel_selection_enabled === true && !guidedOrderId;
  const treatmentTypeSelectionEnabled = clinic?.treatment_type_selection_enabled === true;

  // When a guided order is the entry point, fetch it so we can prefill the
  // treatment type from its program option (intake_form_type matches the
  // dropdown values: trt | hrt | glp1 | peptide | nad | fertility | general).
  const { data: guidedOrderData } = useGuidedOrder(guidedOrderId);
  useEffect(() => {
    if (!guidedOrderId || !treatmentTypeSelectionEnabled) return;
    const intakeType = (guidedOrderData?.data as any)?.clinicProgramOption?.intake_form_type;
    if (intakeType && !treatmentType) {
      setTreatmentType(intakeType);
    }
    // Intentionally exclude `treatmentType` from deps: prefill should not
    // re-fire after the practitioner manually changes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guidedOrderData, guidedOrderId, treatmentTypeSelectionEnabled]);

  // Track if user has interacted with RX Pad (since getPrescriptionValues doesn't sync in real-time)
  const [hasRxPadInteraction, setHasRxPadInteraction] = useState(false);

  // Allergy warning state
  const [showAllergyWarning, setShowAllergyWarning] = useState(false);
  const [allergyWarnings, setAllergyWarnings] = useState<AllergyWarning[]>([]);
  const [allergyWarningsAcknowledged, setAllergyWarningsAcknowledged] = useState(false);
  const [pendingAllergyAction, setPendingAllergyAction] = useState<'rxpad' | 'storefront' | null>(null);

  // Review loading state (allergy + syringe checks)
  const [isReviewing, setIsReviewing] = useState(false);

  // Practitioner dropdown visibility: hide when any selected storefront program
  // is protocol_showcase (default practitioner will be resolved by backend).
  // RX Pad never contains protocol_showcase (filtered at getProgramOptions).
  const hasProtocolShowcase =
    activeTab === 'storefront' &&
    Object.values(selectedMedications).some(sel => {
      const prog = storefrontPrograms.find(p => p.id === sel.program_id);
      return (prog as { display_mode?: string } | undefined)?.display_mode === 'protocol_showcase';
    });
  const showPractitionerDropdown = !hasProtocolShowcase;

  // Syringe warning state
  const [showSyringeWarning, setShowSyringeWarning] = useState(false);
  const [syringeWarningMedications, setSyringeWarningMedications] = useState<string[]>([]);
  const [syringeWarningAcknowledged, setSyringeWarningAcknowledged] = useState(false);
  const [pendingSyringeAction, setPendingSyringeAction] = useState<'rxpad' | 'storefront' | null>(null);

  // Medication drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDrawerMedication, setSelectedDrawerMedication] = useState<any | null>(null);

  const effectivePatientId = patientId || selectedPatient?.id || undefined;

  // Ref to track previous patient for detecting patient changes
  const prevPatientIdRef = useRef<string | undefined>(effectivePatientId);

  // Fetch pre-flattened prescription products from the API
  const { data: prescriptionProductsData, isLoading: isPrescriptionProductsLoading } = useQuery<{ products: PrescriptionProduct[] }>({
    queryKey: ['prescription-products', clinic?.id, effectivePatientId],
    queryFn: async () => {
      return clinicProgramService.getPrescriptionProducts(clinic!.id, effectivePatientId);
    },
    enabled: !!clinic?.id,
    staleTime: 1000 * 60 * 5,
  });

  // Build initial values for RxPadForm when in edit mode
  const editInitialValues = useMemo(() => {
    if (mode !== 'edit' || !initialData) return undefined;
    const rx = initialData;
    const products = prescriptionProductsData?.products || [];

    // Build a deduplicated name+dose -> first product key map
    // This mirrors the deduplication logic in getProductOptions so the resolved key
    // always matches an actual dropdown option value.
    const nameDoseToFirstKey: Record<string, string> = {};
    for (const p of products) {
      const dedupeKey = `${p.medication_name}::${p.dose}`.toLowerCase();
      if (!nameDoseToFirstKey[dedupeKey]) {
        nameDoseToFirstKey[dedupeKey] = p.key;
      }
      // Also index by name-only for fallback
      const nameOnly = p.medication_name.toLowerCase();
      if (!nameDoseToFirstKey[nameOnly]) {
        nameDoseToFirstKey[nameOnly] = p.key;
      }
    }

    // Build product_id -> product lookup (to get name+dose, then resolve to first key)
    const productById: Record<string, typeof products[0]> = {};
    for (const p of products) {
      if (p.product_id && !productById[p.product_id]) {
        productById[p.product_id] = p;
      }
    }

    // Resolve unit CDISC code to label
    const resolveUnit = (unitValue: string | null | undefined): string => {
      if (!unitValue) return '';
      const byCode = medicationUnits.find((u) => u.value === unitValue);
      if (byCode) return byCode.label;
      return unitValue;
    };

    // Parse reason_for_compounding — may be JSON or plain string
    const parseReason = (reason: any): { description: string; context: string } => {
      if (!reason) return { description: '', context: '' };
      if (typeof reason === 'string') {
        try {
          const parsed = JSON.parse(reason);
          return { description: parsed.description || '', context: parsed.context || '' };
        } catch {
          return { description: reason, context: '' };
        }
      }
      if (typeof reason === 'object') {
        return { description: reason.description || '', context: reason.context || '' };
      }
      return { description: '', context: '' };
    };

    return {
      medications: (rx.medicationRequests || []).map((med: any) => {
        const medName = med.medication || med.medication_name || '';
        const medDose = med.dose || '';
        const medProductId = med.product_id || med.product?.id || '';

        // Resolve to a product key that matches a dropdown option:
        // 1. Find product by product_id, then look up its name+dose in the deduplicated map
        // 2. Fall back to matching by name+dose directly
        // 3. Fall back to matching by name only
        let resolvedMedication = medName;
        if (medProductId && productById[medProductId]) {
          const matched = productById[medProductId];
          const key = `${matched.medication_name}::${matched.dose}`.toLowerCase();
          resolvedMedication = nameDoseToFirstKey[key] || medName;
        } else {
          resolvedMedication =
            nameDoseToFirstKey[`${medName}::${medDose}`.toLowerCase()] ||
            nameDoseToFirstKey[medName.toLowerCase()] ||
            medName;
        }

        const reasonData = parseReason(med.reason_for_compounding);

        return {
          medication: resolvedMedication,
          dose: medDose,
          refills: String(med.refills_authorized ?? '0'),
          quantity: String(med.quantity_authorized || ''),
          unit: resolveUnit(med.unit_of_measure),
          sig: med.sig || '',
          reason: reasonData.description,
          additionalContext: reasonData.context || med.reason_for_compounding_context || '',
          daysSupply: String(med.day_supply ?? ''),
          flavor: med.flavor || '',
          notes: med.notes || '',
          program: med.clinicProgram?.id || med.clinic_program_id || '',
          program_id: med.clinicProgram?.id || med.clinic_program_id || '',
          doNotFill: med.do_not_fill ?? false,
          doNotRefill: med.do_not_refill ?? false,
          refillNeeded: false,
          patientContactRequested: med.patient_contact_requested ?? false,
        };
      }),
      priority: rx.priority ?? 1,
    };
  }, [mode, initialData, prescriptionProductsData]);

  // Build initial values for RxPadForm when in create mode with prefilled medications
  const prefillInitialValues = useMemo(() => {
    if (mode !== 'create' || !prefillMedications) return undefined;
    const rx = prefillMedications;
    const products = prescriptionProductsData?.products || [];

    const nameDoseToFirstKey: Record<string, string> = {};
    for (const p of products) {
      const dedupeKey = `${p.medication_name}::${p.dose}`.toLowerCase();
      if (!nameDoseToFirstKey[dedupeKey]) {
        nameDoseToFirstKey[dedupeKey] = p.key;
      }
      const nameOnly = p.medication_name.toLowerCase();
      if (!nameDoseToFirstKey[nameOnly]) {
        nameDoseToFirstKey[nameOnly] = p.key;
      }
    }

    const productById: Record<string, typeof products[0]> = {};
    for (const p of products) {
      if (p.product_id && !productById[p.product_id]) {
        productById[p.product_id] = p;
      }
    }

    const resolveUnit = (unitValue: string | null | undefined): string => {
      if (!unitValue) return '';
      const byCode = medicationUnits.find((u) => u.value === unitValue);
      if (byCode) return byCode.label;
      return unitValue;
    };

    const parseReason = (reason: any): { description: string; context: string } => {
      if (!reason) return { description: '', context: '' };
      if (typeof reason === 'string') {
        try {
          const parsed = JSON.parse(reason);
          return { description: parsed.description || '', context: parsed.context || '' };
        } catch {
          return { description: reason, context: '' };
        }
      }
      if (typeof reason === 'object') {
        return { description: reason.description || '', context: reason.context || '' };
      }
      return { description: '', context: '' };
    };

    return {
      medications: (rx.medicationRequests || []).map((med: any) => {
        const medName = med.medication || med.medication_name || '';
        const medDose = med.dose || '';
        const medProductId = med.product_id || med.product?.id || '';

        // Only preselect if the medication maps to a product in our dropdown.
        // If no match, leave empty so the user picks one explicitly instead of
        // seeing a value that isn't actually in the list.
        let resolvedMedication = '';
        if (medProductId && productById[medProductId]) {
          const matched = productById[medProductId];
          const key = `${matched.medication_name}::${matched.dose}`.toLowerCase();
          resolvedMedication = nameDoseToFirstKey[key] || '';
        } else {
          resolvedMedication =
            nameDoseToFirstKey[`${medName}::${medDose}`.toLowerCase()] ||
            nameDoseToFirstKey[medName.toLowerCase()] ||
            '';
        }

        const reasonData = parseReason(med.reason_for_compounding);

        return {
          medication: resolvedMedication,
          dose: medDose,
          refills: String(med.refills_authorized ?? '0'),
          quantity: String(med.quantity_authorized || ''),
          unit: resolveUnit(med.unit_of_measure),
          sig: med.sig || '',
          reason: reasonData.description,
          additionalContext: reasonData.context || med.reason_for_compounding_context || '',
          daysSupply: String(med.day_supply ?? ''),
          flavor: med.flavor || '',
          notes: med.notes || '',
          program: med.clinicProgram?.id || med.clinic_program_id || '',
          program_id: med.clinicProgram?.id || med.clinic_program_id || '',
          doNotFill: false,
          doNotRefill: false,
          refillNeeded: false,
          patientContactRequested: false,
        };
      }),
      priority: 1,
    };
  }, [mode, prefillMedications, prescriptionProductsData]);

  // Mutation for updating an existing prescription (edit mode)
  const updatePrescriptionMutation = useMutation({
    mutationFn: (data: any) => prescriptionService.updatePrescription(initialData?.id, data),
    onSuccess: (result: any) => {
      toast({
        title: 'Success',
        description: result?.message || 'Order updated successfully',
        variant: 'success',
      });
      onOpenChange(false);
      onSubmit(formData?.patient_id);
      queryClient.invalidateQueries({ queryKey: ['prescriptions'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to update order',
        variant: 'destructive',
      });
    },
  });

  // TanStack Query mutation for saving prescription
  const savePrescriptionMutation = useMutation({
    mutationFn: (data: any) => prescriptionService.savePrescription(data),
    onSuccess: result => {
      if (result.success) {
        const prescriptionId = result?.data?.id;
        if (prescriptionId) {
          // Check for SMART session and sync prescription to FHIR server if available
          const smartSession = getSession();
          if (smartSession?.fhirServer && smartSession?.accessToken) {
            smartService
              .savePrescription(prescriptionId, smartSession.fhirServer, smartSession.accessToken)
              .then(() => {})
              .catch(fhirError => {
                console.error('Failed to sync prescription to FHIR server:', fhirError);
              });
          }
        }
        toast({
          title: 'Success',
          description: result?.message,
          variant: 'success',
        });
        onOpenChange(false);
        onSubmit(formData?.patient_id);

        // Invalidate and refetch prescriptions for this patient
        queryClient.invalidateQueries({
          queryKey: ['prescriptions', patientId],
        });

        // Reset storefront state
        setSelectedProgram(null);
        setSelectedMedications({});
        setDefaultMedicationIds(new Set());
      } else {
        toast({
          title: 'Error',
          description:
            result?.message ?? 'Order creation failed. Please refresh and try again.',
          variant: 'destructive',
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description:
          error?.message ?? 'Order creation failed. Please refresh and try again.',
        variant: 'destructive',
      });
    },
  });

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.patient_id) {
      newErrors.patient_id = 'Patient selection is required';
    }
    // Practitioner is selected on the Review Order screen (not the first page),
    // so it's not checked here. Footer's `practitionerMissing` keeps the final
    // submit button disabled until a practitioner is picked on review.

    setErrors(newErrors);
    console.log(newErrors);

    // Scroll to first error field
    if (Object.keys(newErrors).length > 0) {
      console.log('scrolling to first error field');
      const firstErrorKey = Object.keys(newErrors)[0];
      setTimeout(() => {
        const errorElement =
          document.querySelector(`[data-field="${firstErrorKey}"]`) ||
          document.querySelector(`[name="${firstErrorKey}"]`) ||
          document.getElementById(firstErrorKey);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }

    return Object.keys(newErrors).length === 0;
  };

  // RX Pad - prepare for review
  const handleRxPadReview = async (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();

    if (!validateForm()) return;

    // Use RxPadForm ref to get and validate the data
    if (rxPadRef.current) {
      const medicationData = await rxPadRef.current.submit();

      if (!medicationData) {
        // submitPrescriptionForm returns null/undefined if validation fails
        // Scroll to first error field in RX Pad (within .prescription-pad container)
        setTimeout(() => {
          const rxPadContainer = document.querySelector('.prescription-pad');
          if (rxPadContainer) {
            const errorElement = rxPadContainer.querySelector(
              '.border-red-500, .border-destructive, [data-invalid="true"], [aria-invalid="true"], input:invalid, select:invalid'
            );
            if (errorElement) {
              errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // Focus the element if it's an input
              if (
                errorElement instanceof HTMLInputElement ||
                errorElement instanceof HTMLSelectElement
              ) {
                errorElement.focus();
              }
            }
          }
        }, 100);
        return;
      }

      const medications = medicationData?.medications || [];
      if (medications.length === 0) {
        toast({
          title: 'No medications added',
          description: 'Please add at least one medication to create an order.',
          variant: 'destructive',
        });
        return;
      }

      // Store the medications data for final submit
      setRxPadMedicationsData(medications);

      // Set the priority from RX Pad
      if (medicationData?.priority !== undefined) {
        setPriorityLevel(medicationData.priority);
      }

      // Convert to review medications format
      const reviewMeds: ReviewMedication[] = medications.map((med: any) => ({
        medication_name: med.medication || '',
        dose: med.dose || '',
        sig: med.sig || '',
        quantity: String(med.quantity || 1),
        days_supply: String(med.daysSupply || 30),
        refills: String(med.refills || 0),
        reason: med.reason || '',
        unit_of_measure: med.unit || '',
        flavor: med.flavor || '',
        notes: med.notes || '',
        additional_context: med.additionalContext || '',
        patient_contact_requested: med.patientContactRequested || false,
        do_not_refill: med.doNotRefill || false,
        refill_needed: med.refillNeeded || false,
        do_not_fill: med.doNotFill || false,
        price: med.price ?? med.product?.price,
        base_price: med.base_price ?? med.product?.base_price,
        discount: 0,
      }));

      setReviewMedications(reviewMeds);
      setPriceWarningAcknowledged(false);

      setIsReviewing(true);
      try {
        // Allergy interaction check
        if (!allergyWarningsAcknowledged && formData?.patient_id) {
          try {
            const productIds = medications
              .map((m: any) => m.product_id)
              .filter(Boolean);
            const medicationNames = medications
              .map((m: any) => m.medication)
              .filter(Boolean);
            const result = await checkAllergyInteractions(
              formData.patient_id,
              productIds,
              medicationNames,
            );
            if (result.hasWarnings) {
              setAllergyWarnings(result.warnings);
              setPendingAllergyAction('rxpad');
              setShowAllergyWarning(true);
              return;
            }
          } catch (err) {
            console.error('Allergy check failed, proceeding:', err);
          }
        }

        // Syringe check: warn if injectable prescribed without syringe
        if (!syringeWarningAcknowledged) {
          const productMap = prescriptionProductsData?.products || [];
          const selectedProducts = medications.map((med: any) => {
            const matched = productMap.find((p: PrescriptionProduct) => p.product_id === med.product_id);
            return {
              product_type: matched?.product_type || null,
              name: med.medication || matched?.medication_name || '',
            };
          });
          const syringeResult = checkSyringeNeeded(selectedProducts);
          if (syringeResult.needsWarning) {
            setSyringeWarningMedications(syringeResult.injectableMedications);
            setPendingSyringeAction('rxpad');
            setShowSyringeWarning(true);
            return;
          }
        }

        setShowReview(true);
      } finally {
        setIsReviewing(false);
      }
    } else {
      toast({
        title: 'Error',
        description: 'Unable to get order data. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // RX Pad - proceed after review (either to followup lab or submit)
  const handleRxPadProceedFromReview = () => {
    if (labPanelSelectionEnabled) {
      setShowFollowupLab(true);
    } else {
      handleRxPadSubmit();
    }
  };

  // RX Pad - final submit after review (and optionally followup lab)
  const handleRxPadSubmit = async () => {
    if (rxPadMedicationsData.length === 0) {
      toast({
        title: 'Error',
        description: 'No medication data found. Please go back and try again.',
        variant: 'destructive',
      });
      return;
    }

    // Distribute total discount proportionally across medication line items
    const subtotal = reviewMedications.reduce((s, m) => s + (Number(m.price) || 0) * (Number(m.quantity) || 1), 0);
    let discountRemainingCents = Math.round(totalDiscount * 100);

    const submitData: any = {
      patientId: formData?.patient_id,
      ...(formData?.practitioner_identifier ? { practitionerId: formData.practitioner_identifier } : {}),
      clinicId: clinic?.id || selectedPatient?.clinic_id,
      // Nimbus path: backend auto-verifies + captures when this is set.
      ...(guidedOrderId ? { guided_order_id: guidedOrderId } : {}),
      // Encounter linkage (only when this Rx is being written inside an
      // active visit). Service auto-fills off_encounter_reason='direct_rx'
      // when both are absent.
      ...(encounterId ? { encounter_id: encounterId } : {}),
      ...(appointmentId ? { appointment_id: appointmentId } : {}),
      medicationRequests: rxPadMedicationsData.map((med: any, idx: number) => {
        const reviewMed = reviewMedications[idx];
        const lineTotal = (Number(reviewMed?.price) || 0) * (Number(med.quantity) || 1);
        let lineDiscountCents = 0;
        if (subtotal > 0 && totalDiscount > 0) {
          if (idx === rxPadMedicationsData.length - 1) {
            lineDiscountCents = discountRemainingCents; // last item gets remainder to avoid rounding drift
          } else {
            lineDiscountCents = Math.round((lineTotal / subtotal) * Math.round(totalDiscount * 100));
            discountRemainingCents -= lineDiscountCents;
          }
        }
        return {
          ...med,
          program_id: med.program || '',
          discount_cents: lineDiscountCents,
        };
      }),
      priority: priorityLevel,
      allergyWarningsAcknowledged,
    };

    if (treatmentTypeSelectionEnabled && treatmentType) {
      submitData.treatment_type = treatmentType;
    }

    // Add follow-up lab data if enabled
    if (labPanelSelectionEnabled) {
      if (followupDays) {
        submitData.followup_days = parseInt(followupDays, 10);
      }
      submitData.order_followup_lab = orderFollowupLab;
      if (orderFollowupLab) {
        // Use panelId (for Lab Service) or id (for backward compatibility with Junction)
        submitData.followup_junction_test_id = selectedJunctionTest?.panelId || selectedJunctionTest?.id || null;
      }
    }

    if (mode === 'edit' && initialData?.id) {
      // In edit mode, build update payload with medication IDs for existing meds
      const existingMedIds = (initialData.medicationRequests || []).map((m: any) => m.id);
      const updatePayload: any = {
        priority: priorityLevel,
        medicationRequests: rxPadMedicationsData.map((med: any, index: number) => {
          const daySupplyNum = parseInt(med.daysSupply, 10);
          const reviewMed = reviewMedications[index];
          const lineTotal = (Number(reviewMed?.price) || 0) * (Number(med.quantity) || 1);
          let lineDiscountCents = 0;
          if (subtotal > 0 && totalDiscount > 0) {
            if (index === rxPadMedicationsData.length - 1) {
              lineDiscountCents = discountRemainingCents;
            } else {
              lineDiscountCents = Math.round((lineTotal / subtotal) * Math.round(totalDiscount * 100));
              discountRemainingCents -= lineDiscountCents;
            }
          }
          const data: any = {
            medication: med.medication,
            dose: med.dose,
            quantity_authorized: med.quantity,
            unit_of_measure: med.unit,
            refills_authorized: med.refills,
            sig: med.sig,
            reason_for_compounding: JSON.stringify({
              description: med.reason || '',
              context: med.additionalContext || '',
            }),
            day_supply: isNaN(daySupplyNum) ? null : daySupplyNum,
            flavor: med.flavor || null,
            notes: med.notes || null,
            product_id: med.product_id || null,
            discount_cents: lineDiscountCents,
          };
          // Map back to existing medication request IDs by index
          if (index < existingMedIds.length) {
            data.id = existingMedIds[index];
          }
          return data;
        }),
      };
      updatePrescriptionMutation.mutate(updatePayload);
      return;
    }

    savePrescriptionMutation.mutate(submitData);
  };

  // Storefront - prepare for review
  const handleStorefrontReview = async (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();

    if (!validateForm()) return;

    const selectedMeds = Object.keys(selectedMedications);
    if (selectedMeds.length === 0) {
      toast({
        title: 'No medications selected',
        description: 'Please select at least one medication to create an order.',
        variant: 'destructive',
      });
      return;
    }

    // Check for medications without product_id (skip protocol options — they use unit_of_measure='program')
    const medsWithoutProduct = selectedMeds.filter(medId => {
      const selection = selectedMedications[medId];
      if (selection.unit_of_measure === 'program') return false; // protocol option — no product_id needed
      return !selection.product_id || selection.product_id === '';
    });

    if (medsWithoutProduct.length > 0) {
      const medNames = medsWithoutProduct
        .map(medId => selectedMedications[medId].medication_name)
        .join(', ');
      toast({
        title: 'Product mapping missing',
        description: `The following medications don't have a product mapped: ${medNames}. Please select a valid dose.`,
        variant: 'destructive',
      });
      return;
    }

    // Convert to review medications format
    const reviewMeds: ReviewMedication[] = selectedMeds.map(medId => {
      const selection = selectedMedications[medId];
      return {
        id: medId,
        medication_name: selection.medication_name || '',
        dose: selection.dose || '',
        sig: selection.sig || '',
        quantity: String(selection.quantity || 1),
        days_supply: selection.daysSupply || '30',
        refills: selection.refills_authorized || '0',
        reason: selection.reason || '',
        price: selection.price,
        base_price: selection.base_price,
        program_name: selection.program_name,
        unit_of_measure: selection.unit_of_measure,
      };
    });

    setReviewMedications(reviewMeds);
    setPriceWarningAcknowledged(false);

    setIsReviewing(true);
    try {
      // Allergy interaction check
      if (!allergyWarningsAcknowledged && formData?.patient_id) {
        try {
          const productIds = selectedMeds
            .map((medId) => selectedMedications[medId]?.product_id)
            .filter(Boolean) as string[];
          const medicationNames = selectedMeds
            .map((medId) => selectedMedications[medId]?.medication_name)
            .filter(Boolean) as string[];
          const result = await checkAllergyInteractions(
            formData.patient_id,
            productIds,
            medicationNames,
          );
          if (result.hasWarnings) {
            setAllergyWarnings(result.warnings);
            setPendingAllergyAction('storefront');
            setShowAllergyWarning(true);
            return;
          }
        } catch (err) {
          console.error('Allergy check failed, proceeding:', err);
        }
      }

      // Syringe check: warn if injectable prescribed without syringe
      if (!syringeWarningAcknowledged) {
        const selectedProducts = selectedMeds.map(medId => {
          const selection = selectedMedications[medId];
          return {
            product_type: selection?.product_type || null,
            name: selection?.medication_name || '',
          };
        });
        const syringeResult = checkSyringeNeeded(selectedProducts);
        if (syringeResult.needsWarning) {
          setSyringeWarningMedications(syringeResult.injectableMedications);
          setPendingSyringeAction('storefront');
          setShowSyringeWarning(true);
          return;
        }
      }

      setShowReview(true);
    } finally {
      setIsReviewing(false);
    }
  };

  const handleStorefrontReviewMedicationRemove = (medication: ReviewMedication, index: number) => {
    if (medication.id) {
      setSelectedMedications(prev => {
        const { [medication.id]: removed, ...rest } = prev;
        return rest;
      });
      setReviewMedications(prev => prev.filter(item => item.id !== medication.id));
      return;
    }

    setReviewMedications(prev => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleRxPadReviewMedicationRemove = (_medication: ReviewMedication, index: number) => {
    setRxPadMedicationsData(prev => prev.filter((_, itemIndex) => itemIndex !== index));
    setReviewMedications(prev => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const [totalDiscount, setTotalDiscount] = useState<number>(0);

  const maxDiscount = reviewMedications.reduce((sum, med) => {
    const price = Number(med.price) || 0;
    const basePrice = Number(med.base_price) || 0;
    const qty = Number(med.quantity) || 1;
    return sum + Math.max(0, price - basePrice) * qty;
  }, 0);

  const discountError = Math.round(totalDiscount * 100) > Math.round(maxDiscount * 100);

  // Storefront - proceed after review (either to followup lab or submit)
  const handleStorefrontProceedFromReview = () => {
    if (labPanelSelectionEnabled) {
      setShowFollowupLab(true);
    } else {
      handleStorefrontSubmit();
    }
  };

  // Storefront - final submit after review (and optionally followup lab)
  const handleStorefrontSubmit = async () => {
    const selectedMeds = Object.keys(selectedMedications);

    // Convert selected medications to prescription format (matching RX Pad format)
    const medicationRequests = selectedMeds.map(medId => {
      const selection = selectedMedications[medId];
      const isProtocolOption = selection.unit_of_measure === 'program';

      // Ensure product_id is never undefined
      const productId =
        selection.product_id && selection.product_id !== '' ? selection.product_id : null;

      // Extract clinic_program_option_id from the medId key for protocol options
      const clinicProgramOptionId = isProtocolOption && medId.startsWith('protocol_')
        ? medId.replace('protocol_', '')
        : null;

      return {
        medication: selection.medication_name || '',
        dose: selection.dose || '',
        refills: selection.refills_authorized || '0',
        quantity: String(selection.quantity || 1),
        unit: medicationUnits.find((u) => u.value === selection.unit_of_measure)?.label || selection.unit_of_measure || '',
        sig: selection.sig || '',
        reason: selection.reason || `${selection.program_name} - Order`,
        additionalContext: '',
        patientContactRequested: false,
        doNotRefill: false,
        refillNeeded: false,
        doNotFill: false,
        program_id: selection.program_id || null,
        clinic_program_id: clinic?.id ? selection.program_id || null : null,
        clinic_program_option_id: clinicProgramOptionId,
        daysSupply: isProtocolOption ? '' : (selection.daysSupply || '30'),
        flavor: '',
        notes: '',
        product_id: isProtocolOption ? null : productId,
        template_id: isProtocolOption ? null : (selection.template_id || null),
      };
    });

    // Distribute total discount proportionally across medication line items
    const sfSubtotal = reviewMedications.reduce((s, m) => s + (Number(m.price) || 0) * (Number(m.quantity) || 1), 0);
    let sfDiscountRemainingCents = Math.round(totalDiscount * 100);

    const medicationRequestsWithDiscount = medicationRequests.map((med, idx) => {
      const reviewMed = reviewMedications[idx];
      const lineTotal = (Number(reviewMed?.price) || 0) * (Number(med.quantity) || 1);
      let lineDiscountCents = 0;
      if (sfSubtotal > 0 && totalDiscount > 0) {
        if (idx === medicationRequests.length - 1) {
          lineDiscountCents = sfDiscountRemainingCents;
        } else {
          lineDiscountCents = Math.round((lineTotal / sfSubtotal) * Math.round(totalDiscount * 100));
          sfDiscountRemainingCents -= lineDiscountCents;
        }
      }
      return {
        ...med,
        discount_cents: lineDiscountCents,
      };
    });

    const submitData: any = {
      patientId: formData?.patient_id,
      ...(formData?.practitioner_identifier ? { practitionerId: formData.practitioner_identifier } : {}),
      clinicId: clinic?.id || selectedPatient?.clinic_id,
      // Encounter linkage flows from active visit if present.
      ...(encounterId ? { encounter_id: encounterId } : {}),
      ...(appointmentId ? { appointment_id: appointmentId } : {}),
      medicationRequests: medicationRequestsWithDiscount,
      priority: priorityLevel,
      allergyWarningsAcknowledged,
    };

    if (treatmentTypeSelectionEnabled && treatmentType) {
      submitData.treatment_type = treatmentType;
    }

    // Add follow-up lab data if enabled
    if (labPanelSelectionEnabled) {
      if (followupDays) {
        submitData.followup_days = parseInt(followupDays, 10);
      }
      submitData.order_followup_lab = orderFollowupLab;
      if (orderFollowupLab && selectedJunctionTest) {
        // Use panelId (for Lab Service) or id (for backward compatibility with Junction)
        submitData.followup_junction_test_id = selectedJunctionTest.panelId || selectedJunctionTest.id;
      }
    }

    savePrescriptionMutation.mutate(submitData);
  };

  const handlePatientSelect = (patient: Patient) => {
    if (patient) {
      setSelectedPatient(patient);
      setFormData(prev => ({ ...prev, patient_id: patient?.id || null }));
    }
  };

  // Fetch storefront programs
  const fetchStorefrontPrograms = async () => {
    try {
      setLoadingPrograms(true);
      const response = await getPrograms({
        page: 1,
        limit: 20,
        sortBy: 'title',
        sortOrder: 'ASC',
        search: searchTerm,
        clinicId: clinic?.id,
      });
      // Hide guided (protocol_showcase) programs — they don't belong in the
      // direct-prescribing drawer; they go through the guided/MDI checkout flow.
      setStorefrontPrograms(
        (response.programs || []).filter(p => p.display_mode !== 'protocol_showcase'),
      );
    } catch (err) {
      console.error('Error fetching programs:', err);
      toast({
        title: 'Error',
        description: 'Failed to load programs',
        variant: 'destructive',
      });
    } finally {
      setLoadingPrograms(false);
    }
  };

  // Fetch program details
  const fetchProgramDetails = async (programId: string) => {
    try {
      setLoadingProgramDetails(true);
      const programData = await getProgramDetails(programId, clinic?.id, undefined, effectivePatientId);

      // Flatten medications into product-level entries (one per medication+dose)
      const flattenedMedications: any[] = [];
      for (const med of (programData.medications || [])) {
        const doses = (med.doses || []).filter((d: string) => d && d.trim() !== '');
        // If product has no template doses, treat it as a single product entry
        if (doses.length === 0) {
          const product = med.map_dose_to_product?.[0];
          flattenedMedications.push({
            ...med,
            id: med.id,
            originalMedId: med.id,
            name: med.name,
            dose: '',
            doses: [],
            sig: med.sig || [],
            qty_options: med.qty_options || [],
            unit_of_measure: med.unit_of_measure || null,
            product_id: product?.product_id || med.product_id || null,
            price: parseFloat(product?.price || med.price || '0'),
            base_price: parseFloat(product?.base_price || med.base_price || '0'),
            image_url: product?.image_url || med.image_url || null,
            map_dose_to_product: product ? [product] : [],
          });
          continue;
        }
        for (const dose of doses) {
          const product = med.map_dose_to_product?.find((p: any) => p.dose === dose);
          const displayName = med.name;
          flattenedMedications.push({
            ...med,
            id: `${med.id}::${dose}`,
            originalMedId: med.id,
            name: displayName,
            dose,
            doses: [dose],
            sig: med.sig || [],
            qty_options: med.qty_options || [],
            unit_of_measure: med.unit_of_measure || null,
            product_id: product?.product_id || null,
            price: parseFloat(product?.price || '0'),
            base_price: parseFloat(product?.base_price || '0'),
            image_url: product?.image_url || med.image_url || null,
            map_dose_to_product: product ? [product] : [],
          });
        }
      }
      programData.medications = flattenedMedications;
      setSelectedProgram(programData);

      // Skip default-medication auto-selection for guided (protocol_showcase)
      // programs — those route through the protocol option picker, not the
      // per-medication flow. Stale defaults may exist from when the program
      // was 'medication_select', so we explicitly ignore them here.
      const isProtocolShowcase = programData?.display_mode === 'protocol_showcase';

      // Fetch and apply default medications for this program
      const existingSelectionsFromProgram = Object.values(selectedMedications).filter(
        sel => sel.program_id === programId
      );

      if (!isProtocolShowcase && existingSelectionsFromProgram.length === 0) {
        try {
          const defaultMeds = await getProgramDefaultMedications(
            programId,
            clinic?.id,
            undefined,
            effectivePatientId
          );
          if (defaultMeds && defaultMeds.length > 0) {
            const preselectedMedications: Record<string, MedicationSelection> = {};

            defaultMeds.forEach((defaultMed: DefaultMedication) => {
              // Match by product ID (medication_id is now a DB product UUID)
              // First try exact match with dose, then fallback to product ID only
              const medication = programData.medications?.find(
                (m: any) =>
                  (m.originalMedId === defaultMed.medication_id ||
                    m.originalMedId === defaultMed.product_id ||
                    m.id === defaultMed.medication_id ||
                    m.id === defaultMed.product_id) &&
                  (!m.dose || !defaultMed.dose || m.dose === defaultMed.dose)
              ) || programData.medications?.find(
                (m: any) =>
                  m.originalMedId === defaultMed.medication_id ||
                  m.originalMedId === defaultMed.product_id ||
                  m.id === defaultMed.medication_id ||
                  m.id === defaultMed.product_id
              );

              if (medication) {
                const product = medication.map_dose_to_product?.find(
                  (p: any) => p.dose === defaultMed.dose
                ) || medication.map_dose_to_product?.[0];
                const defaultReason = `${programData.title} - ${programData.subtitle || 'Order program'}`;

                preselectedMedications[medication.id] = {
                  quantity: defaultMed.quantity || 1,
                  dose: defaultMed.dose || medication.dose || '',
                  sig: defaultMed.sig,
                  product_id: product?.product_id || medication.product_id || defaultMed.product_id || '',
                  price: parseFloat(product?.price || medication.price || '0'),
                  base_price: parseFloat(product?.base_price || medication.base_price || '0'),
                  program_name: programData.title,
                  program_id: programData.id,
                  medication_name: medication.name,
                  daysSupply: defaultMed.days_supply || '30',
                  reason: product?.compounding_reason || defaultReason,
                  unit_of_measure: medication?.unit_of_measure || '',
                  refills_authorized: String(defaultMed.refills_authorized || 0),
                  template_id: medication.template_id || null,
                  product_type: product?.product_type || medication?.product_type || null,
                };
              }
            });

            if (Object.keys(preselectedMedications).length > 0) {
              setSelectedMedications(prev => ({
                ...prev,
                ...preselectedMedications,
              }));
              setDefaultMedicationIds(
                prev => new Set([...prev, ...Object.keys(preselectedMedications)])
              );
            }
          }
        } catch (defaultsErr) {
          console.warn('Could not load default medications:', defaultsErr);
        }
      }
    } catch (err) {
      console.error('Error fetching program details:', err);
      toast({
        title: 'Error',
        description: 'Failed to load program details',
        variant: 'destructive',
      });
    } finally {
      setLoadingProgramDetails(false);
    }
  };

  // Medication handlers
  const handleMedicationClick = (medication: any) => {
    setSelectedDrawerMedication(medication);
    setDrawerOpen(true);
  };

  const handleMedicationRemove = (medication: any) => {
    setSelectedMedications(prev => {
      const { [medication.id]: removed, ...rest } = prev;
      return rest;
    });
  };

  // When patient changes, reset forms and refetch program details for the new patient
  useEffect(() => {
    if (prevPatientIdRef.current === effectivePatientId) return;
    prevPatientIdRef.current = effectivePatientId;

    // Reset RX Pad form
    rxPadRef.current?.reset();

    // Reset storefront state and refetch if a program is selected
    if (selectedProgram?.id && effectivePatientId) {
      setSelectedMedications({});
      setDefaultMedicationIds(new Set());
      fetchProgramDetails(selectedProgram.id);
    }
  }, [effectivePatientId]);

  const handleMedicationSubmitFromDrawer = (selection: SharedMedicationSelection) => {
    // Update selectedMedications with the configured selection
    setSelectedMedications(prev => ({
      ...prev,
      [selectedDrawerMedication.id]: {
        ...selection,
        medication_name: selectedDrawerMedication.name,
      },
    }));
  };

  // Protocol option selection handler (for protocol_showcase programs)
  const handleProtocolOptionSelect = (option: any) => {
    if (!selectedProgram) return;

    // Toggle selection — deselect if same option clicked
    if (selectedProtocolOptionId === option.id) {
      setSelectedProtocolOptionId(null);
      // Remove the protocol entry from selectedMedications
      setSelectedMedications(prev => {
        const { [`protocol_${option.id}`]: removed, ...rest } = prev;
        return rest;
      });
      return;
    }

    // Clear any previous protocol selection from this program
    setSelectedMedications(prev => {
      const cleaned: Record<string, MedicationSelection> = {};
      Object.entries(prev).forEach(([key, val]) => {
        if (!key.startsWith('protocol_') || val.program_id !== selectedProgram.id) {
          cleaned[key] = val;
        }
      });
      return {
        ...cleaned,
        [`protocol_${option.id}`]: {
          quantity: 1,
          dose: `$${(option.program_price / 100).toFixed(2)}`,
          sig: option.is_subscription
            ? `${option.subscription_frequency || 'recurring'} subscription`
            : 'one-time',
          product_id: '',
          price: option.program_price / 100,
          base_price: option.program_price / 100,
          program_name: selectedProgram.title,
          program_id: selectedProgram.id,
          medication_name: option.label,
          daysSupply: '',
          reason: `${selectedProgram.title} - Protocol`,
          unit_of_measure: 'program',
          refills_authorized: '0',
          template_id: null,
        },
      };
    });

    setSelectedProtocolOptionId(option.id);
  };

  const getSelectedCount = () => Object.keys(selectedMedications).length;

  const hasPriceWarning = reviewMedications.some(
    (med) =>
      med.price != null &&
      med.base_price != null &&
      med.base_price > 0 &&
      med.price < med.base_price
  );
  const fetchPatientFromId = async () => {
    try {
      const response = await patientService.getPatientById(patientId);
      console.log(response);
      if (response.success && response.data) {
        const { email, phone } = getDefaultEmailAndPhone(response.data.contacts);
        const patient = {
          ...response.data,
          phone,
          email,
        };
        setSelectedPatient(patient);
      }
    } catch (error) {
      console.error('Error fetching patient:', error);
    }
  };

  // Fetch patient data when patientId is provided as prop
  useEffect(() => {
    if (patientId && open) {
      fetchPatientFromId();
    }
  }, [patientId, open]);

  useEffect(() => {
    if (initialData && mode === 'edit') {
      // Populate form with initial data for edit mode
    } else {
      // Reset form for create mode - but keep patient if patientId is provided
      if (!patientId) {
        setSelectedPatient(null);
      }
      setFormData({
        patient_id: patientId || null,
        practitioner_identifier: practitionerId || null,
        medication_requests: [
          {
            medication: '',
            dose: '',
            quantity_authorized: '',
            refills_authorized: '',
            unit_of_measure: '',
            sig: '',
            date_issued: new Date().toISOString().split('T')[0],
            flavor: '',
            note: '',
            reason_for_compounding: '',
            do_not_fill: false,
            do_not_refill: false,
            refill_as_needed: false,
            patient_contact_requested: false,
            product_id: null,
            template_id: null,
          },
        ],
      });
      setSelectedProgram(null);
      setSelectedMedications({});
      setDefaultMedicationIds(new Set());
      setSearchTerm('');
      setShowReview(false);
      setReviewMedications([]);
      setRxPadMedicationsData([]);
      setPriorityLevel(1);
      setHasRxPadInteraction(false);
      // Reset followup lab state
      setShowFollowupLab(false);
      setFollowupDays('');
      setOrderFollowupLab(false);
      setSelectedJunctionTest(null);
      // Reset allergy warning state
      setAllergyWarningsAcknowledged(false);
      setAllergyWarnings([]);
      setShowAllergyWarning(false);
      setPendingAllergyAction(null);
      // Reset syringe warning state
      setSyringeWarningAcknowledged(false);
      setSyringeWarningMedications([]);
      setShowSyringeWarning(false);
      setPendingSyringeAction(null);
    }
    setErrors({});
  }, [initialData, mode, open]);

  useEffect(() => {
    if (!clinic?.id) return;

    getAllPrograms()
      .then(fetchedPrograms => {
        setPrograms(fetchedPrograms || []);
      })
      .catch(error => {
        console.error('Error fetching programs:', error);
        toast({
          title: 'Error',
          description: 'Failed to load programs',
          variant: 'destructive',
        });
      });
  }, [clinic?.id]);

  useEffect(() => {
    if (open && activeTab === 'storefront' && clinic?.id) {
      fetchStorefrontPrograms();
    }
  }, [open, activeTab, searchTerm, clinic?.id]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      validateForm();
    }
  }, [formData?.patient_id, formData?.practitioner_identifier]);

  // Filter valid medications and sort to show default medications at the top
  const medications = (
    selectedProgram?.medications?.filter(
      (medication: any) => medication.name && (
        (medication.doses && medication.doses.length > 0) ||
        medication.product_id
      )
    ) || []
  ).sort((a: any, b: any) => {
    const aIsDefault = defaultMedicationIds.has(a.id) ? 1 : 0;
    const bIsDefault = defaultMedicationIds.has(b.id) ? 1 : 0;
    return bIsDefault - aIsDefault;
  });

  return (
    <TooltipProvider>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className='flex w-full !max-w-[720px] flex-col gap-0 rounded-l-2xl p-0 [&>button]:hidden'>
          {/* Header */}
          <div className='flex items-center justify-between border-b border-slate-200 px-6 py-4'>
            <div className='flex items-center space-x-3'>
              <div className='flex h-10 w-10 items-center justify-center rounded-full bg-teal-100'>
                <Pill className='h-5 w-5 text-teal-600' />
              </div>
              <div>
                <SheetTitle
                  className='font-semibold text-slate-900'
                  style={{ fontSize: '1.05rem' }}
                >
                  {mode === 'create' ? 'Create New Order' : 'Edit Order'}
                </SheetTitle>
                <p className='mt-0.5 text-sm text-slate-500'>
                  {mode === 'create'
                    ? 'Create a new order for a patient'
                    : 'Update order information'}
                </p>
              </div>
            </div>
            <SheetClose asChild>
              <button className='flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 transition-colors hover:bg-slate-50'>
                <X className='h-5 w-5 text-slate-600' />
              </button>
            </SheetClose>
          </div>

          {/* IntelliHealth Rx Safety Banner */}
          {selectedPatient?.id && (
            <IntelliHealthRxBanner patientId={selectedPatient.id} />
          )}

          {/* Content */}
          <div className='flex-1 overflow-y-auto px-6 py-6'>
            {/* Follow-up Lab Step */}
            {showFollowupLab && (
              <FollowupLabStep
                followupDays={followupDays}
                onFollowupDaysChange={setFollowupDays}
                orderFollowupLab={orderFollowupLab}
                onOrderFollowupLabChange={setOrderFollowupLab}
                selectedTest={selectedJunctionTest}
                onSelectedTestChange={setSelectedJunctionTest}
                onBack={() => setShowFollowupLab(false)}
              />
            )}

            {/* Review Screen */}
            {showReview && !showFollowupLab && (
              <PrescriptionReviewScreen
                medications={reviewMedications}
                patientName={selectedPatient?.full_name}
                practitionerName={
                  selectedPractitioner?.full_name ||
                  (isPractitioner ? `${user?.firstName} ${user?.lastName}` : '')
                }
                showPractitionerDropdown={showPractitionerDropdown}
                selectedPractitionerId={formData.practitioner_identifier}
                onPractitionerSelect={practitioner => {
                  setFormData(prev => ({ ...prev, practitioner_identifier: practitioner.id }));
                  setSelectedPractitioner(practitioner);
                }}
                practitionerError={errors.practitioner_identifier}
                practitionerLoading={isLoading}
                treatmentTypeEnabled={treatmentTypeSelectionEnabled}
                treatmentType={treatmentType}
                onTreatmentTypeChange={setTreatmentType}
                priorityLevel={priorityLevel}
                onPriorityChange={newPriority => {
                  setPriorityLevel(newPriority);
                }}
                onBack={() => setShowReview(false)}
                onRemoveMedication={
                  activeTab === 'storefront'
                    ? handleStorefrontReviewMedicationRemove
                    : handleRxPadReviewMedicationRemove
                }
                discount={totalDiscount}
                onDiscountChange={setTotalDiscount}
                discountError={discountError}
                priceWarningAcknowledged={priceWarningAcknowledged}
                onPriceWarningAcknowledgedChange={setPriceWarningAcknowledged}
              />
            )}

            {/* Regular Content (hidden with CSS when reviewing/followup to preserve RX Pad state) */}
            <div className={showReview || showFollowupLab ? 'hidden' : ''}>
              {/* Patient Selection - show selector if no patientId, show info if patientId provided */}
              {!patientId ? (
                <PatientSelection
                  selectedPatient={selectedPatient}
                  onPatientSelect={handlePatientSelect}
                  error={errors.patient_id}
                />
              ) : selectedPatient ? (
                <div className='mb-6 space-y-2'>
                  <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>Patient</h3>
                  <div className='flex items-center gap-3 rounded-lg border border-teal-200 bg-teal-50 p-3'>
                    <div className='flex h-10 w-10 items-center justify-center rounded-full bg-teal-100'>
                      <User className='h-5 w-5 text-teal-600' />
                    </div>
                    <div className='flex-1'>
                      <p className='font-medium text-slate-900'>
                        {selectedPatient.full_name ||
                          `${selectedPatient.first_name} ${selectedPatient.last_name}`}
                      </p>
                      <div className='mt-2 grid grid-cols-2 gap-x-6 gap-y-1.5'>
                        {selectedPatient?.phone && (
                          <div className='flex items-center gap-2 text-sm text-slate-600'>
                            <Phone className='h-3.5 w-3.5 text-slate-400' />
                            <span>{selectedPatient?.phone || 'No phone'}</span>
                          </div>
                        )}
                        {selectedPatient?.email && (
                          <div className='flex items-center gap-2 text-sm text-slate-600'>
                            <Mail className='h-3.5 w-3.5 text-slate-400' />
                            <span>{selectedPatient?.email}</span>
                          </div>
                        )}
                        {selectedPatient?.dob && (
                          <div className='flex items-center gap-2 text-sm text-slate-600'>
                            <Calendar className='h-3.5 w-3.5 text-slate-400' />
                            <span>
                              {formatDOB(selectedPatient.dob)}
                            </span>
                          </div>
                        )}
                        {selectedPatient?.gender && (
                          <div className='flex items-center gap-2 text-sm text-slate-600'>
                            <User className='h-3.5 w-3.5 text-slate-400' />
                            <span>{toTitleCase(selectedPatient?.gender || '')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <CheckCircle className='h-5 w-5 text-teal-600' />
                  </div>
                </div>
              ) : (
                <div className='mb-6 space-y-2'>
                  <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>Patient</h3>
                  <div className='flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3'>
                    <div className='h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-teal-600' />
                    <p className='text-sm text-slate-500'>Loading patient...</p>
                  </div>
                </div>
              )}

              {/* Gate: require patient selection before showing rest of the form */}
              {!effectivePatientId ? (
                <div className='rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-8 text-center'>
                  <User className='mx-auto h-10 w-10 text-slate-300 mb-3' />
                  <p className='text-sm font-medium text-slate-600'>Select a patient to continue</p>
                  <p className='text-xs text-slate-400 mt-1'>
                    Patient selection is required to load available medications and programs.
                  </p>
                </div>
              ) : (
              <>
              {/* Medication Requests with Tabs */}
              <div className='space-y-4'>
                <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
                  Medication Requests
                </h3>

                <Tabs
                  value={activeTab}
                  onValueChange={value => {
                    const hasStorefrontData = Object.keys(selectedMedications).length > 0;

                    let hasRxPadData = false;
                    if (activeTab === 'rxpad') {
                      // First check if user has interacted with RX Pad (more reliable)
                      if (hasRxPadInteraction) {
                        hasRxPadData = true;
                      } else if (rxPadRef.current) {
                        // Fall back to checking rxPadRef values
                        try {
                          const rxPadValues = rxPadRef.current.getValues();
                          const medications = rxPadValues?.medications || [];
                          hasRxPadData = medications.some((med: any) => {
                            return (
                              (med.medication && med.medication.length > 0) ||
                              (med.dose && med.dose.length > 0) ||
                              (med.sig && med.sig.length > 0) ||
                              (med.reason && med.reason.length > 0) ||
                              (med.quantity && med.quantity.length > 0) ||
                              (med.daysSupply && med.daysSupply.length > 0) ||
                              (med.refills && med.refills.length > 0) ||
                              (med.notes && med.notes.length > 0) ||
                              (med.flavor && med.flavor.length > 0) ||
                              (med.additionalContext && med.additionalContext.length > 0) ||
                              (med.program && med.program.length > 0)
                            );
                          });
                        } catch (e) {
                          hasRxPadData = false;
                        }
                      }
                    }

                    const shouldShowWarning =
                      (activeTab === 'storefront' && hasStorefrontData) ||
                      (activeTab === 'rxpad' && hasRxPadData);

                    if (shouldShowWarning) {
                      setPendingTabValue(value as 'storefront' | 'rxpad');
                      setShowTabSwitchConfirm(true);
                      return;
                    }

                    if (value === 'storefront') {
                      setSelectedMedications({});
                      setDefaultMedicationIds(new Set());
                      setSelectedProgram(null);
                      setSelectedProtocolOptionId(null);
                    }

                    setActiveTab(value as 'storefront' | 'rxpad');
                  }}
                  className='w-full'
                >
                  {!isGuided && !isExternalPatient && !guidedOrderId && mode !== 'edit' && !prefillMedications && (
                  <TabsList className='mb-4 grid w-full grid-cols-2'>
                    <TabsTrigger value='storefront' className='flex items-center gap-2'>
                      <Store className='h-4 w-4' />
                      Store Front
                    </TabsTrigger>
                    <TabsTrigger value='rxpad' className='flex items-center gap-2'>
                      <ClipboardList className='h-4 w-4' />
                      RX Pad
                    </TabsTrigger>
                  </TabsList>
                  )}

                  {/* Store Front Tab */}
                  <TabsContent value='storefront' className='mt-0'>
                    {!selectedProgram ? (
                      <ProgramsList
                        programs={storefrontPrograms}
                        selectedMedications={selectedMedications}
                        searchTerm={searchTerm}
                        loading={loadingPrograms}
                        priorityLevel={priorityLevel}
                        onSearchChange={setSearchTerm}
                        onProgramSelect={fetchProgramDetails}
                        onClearSelections={() => {
                          setSelectedMedications({});
                          setDefaultMedicationIds(new Set());
                        }}
                        onRemoveMedication={medId => {
                          setSelectedMedications(prev => {
                            const { [medId]: removed, ...rest } = prev;
                            return rest;
                          });
                        }}
                        onPriorityChange={setPriorityLevel}
                      />
                    ) : (
                      <ProgramDetails
                        program={selectedProgram}
                        medications={medications}
                        selectedMedications={selectedMedications}
                        defaultMedicationIds={defaultMedicationIds}
                        onBack={() => {
                          setSelectedProgram(null);
                          setSelectedProtocolOptionId(null);
                        }}
                        onMedicationClick={handleMedicationClick}
                        onMedicationRemove={handleMedicationRemove}
                        loading={loadingProgramDetails}
                        onProtocolOptionSelect={handleProtocolOptionSelect}
                        selectedProtocolOptionId={selectedProtocolOptionId}
                      />
                    )}
                  </TabsContent>

                  {/* RX Pad Tab */}
                  {!isGuided && (
                  <TabsContent value='rxpad' className='mt-0'>
                    <div className='prescription-pad'>
                      {isPrescriptionProductsLoading && (
                        <div className='flex items-center justify-center py-8 text-slate-500'>
                          <div className='mr-2 h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-brand-teal' />
                          Loading products...
                        </div>
                      )}
                      {!isPrescriptionProductsLoading && (
                        <div
                          onFocusCapture={() => setHasRxPadInteraction(true)}
                          onInputCapture={() => setHasRxPadInteraction(true)}
                        >
                          <RxPadForm
                            ref={rxPadRef}
                            programs={programs}
                            prescriptionProducts={prescriptionProductsData?.products || []}
                            initialValues={editInitialValues || prefillInitialValues}
                          />
                        </div>
                      )}
                    </div>
                  </TabsContent>
                  )}
                </Tabs>
              </div>
              </>
              )}
            </div>
          </div>

          {/* Footer */}
          <PrescriptionFooter
            showReview={showReview}
            showFollowupLab={showFollowupLab}
            activeTab={activeTab}
            selectedCount={getSelectedCount()}
            reviewCount={reviewMedications.length}
            mode={mode}
            isSubmitting={savePrescriptionMutation.isPending || updatePrescriptionMutation.isPending}
            labPanelSelectionEnabled={labPanelSelectionEnabled}
            orderFollowupLab={orderFollowupLab}
            selectedJunctionTest={selectedJunctionTest}
            onCancel={() => onOpenChange(false)}
            onBackToEdit={() => setShowReview(false)}
            onBackToReview={() => setShowFollowupLab(false)}
            onReview={activeTab === 'storefront' ? handleStorefrontReview : handleRxPadReview}
            onProceedFromReview={
              activeTab === 'storefront'
                ? handleStorefrontProceedFromReview
                : handleRxPadProceedFromReview
            }
            onSubmit={activeTab === 'storefront' ? handleStorefrontSubmit : handleRxPadSubmit}
            hasPriceWarning={hasPriceWarning}
            priceWarningAcknowledged={priceWarningAcknowledged}
            discountError={discountError}
            isReviewing={isReviewing}
            treatmentTypeMissing={treatmentTypeSelectionEnabled && !treatmentType}
            practitionerMissing={showPractitionerDropdown && !formData.practitioner_identifier}
          />
        </SheetContent>
      </Sheet>

      {/* Tab Switch Confirmation Dialog */}
      <ConfirmDialog
        open={showTabSwitchConfirm}
        onOpenChange={open => {
          setShowTabSwitchConfirm(open);
          if (!open) setPendingTabValue(null);
        }}
        title='Switch Tab?'
        description='Switching tabs will clear your current selections. Are you sure you want to continue?'
        onConfirm={() => {
          if (pendingTabValue) {
            if (pendingTabValue === 'storefront') {
              setSelectedMedications({});
              setDefaultMedicationIds(new Set());
              setSelectedProgram(null);
            }
            // Reset RX Pad interaction tracking when switching away from RX Pad
            if (activeTab === 'rxpad') {
              setHasRxPadInteraction(false);
            }
            setActiveTab(pendingTabValue);
            setPendingTabValue(null);
          }
        }}
      />

      {/* Medication Drawer */}
      {selectedProgram && (
        <MedicationDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          medication={selectedDrawerMedication}
          programData={selectedProgram}
          onSubmit={handleMedicationSubmitFromDrawer}
          submitButtonText='Add to Order'
          mode='prescription'
          existingSelection={
            selectedDrawerMedication ? selectedMedications[selectedDrawerMedication.id] : null
          }
        />
      )}
      {/* Allergy Warning Dialog */}
      <AllergyWarningDialog
        open={showAllergyWarning}
        onOpenChange={setShowAllergyWarning}
        warnings={allergyWarnings}
        patientName={
          selectedPatient
            ? selectedPatient.full_name || `${selectedPatient.first_name} ${selectedPatient.last_name}`
            : undefined
        }
        patientAllergies={structuredAllergyDisplays.length > 0
          ? structuredAllergyDisplays
          : (selectedPatient?.medication_allergies as string[] | undefined)
        }
        onGoBack={() => {
          setShowAllergyWarning(false);
          setPendingAllergyAction(null);
        }}
        onAcknowledge={() => {
          setAllergyWarningsAcknowledged(true);
          setShowAllergyWarning(false);

          // Chain into syringe check before proceeding to review
          if (!syringeWarningAcknowledged) {
            let selectedProducts: { product_type?: string | null; name?: string }[] = [];
            if (pendingAllergyAction === 'rxpad') {
              const productMap = prescriptionProductsData?.products || [];
              selectedProducts = rxPadMedicationsData.map((med: any) => {
                const matched = productMap.find((p: PrescriptionProduct) => p.product_id === med.product_id);
                return { product_type: matched?.product_type || null, name: med.medication || matched?.medication_name || '' };
              });
            } else if (pendingAllergyAction === 'storefront') {
              selectedProducts = Object.values(selectedMedications).map((sel: any) => ({
                product_type: sel.product_type || null,
                name: sel.medication_name || '',
              }));
            }
            const syringeResult = checkSyringeNeeded(selectedProducts);
            if (syringeResult.needsWarning) {
              setSyringeWarningMedications(syringeResult.injectableMedications);
              setPendingSyringeAction(pendingAllergyAction);
              setShowSyringeWarning(true);
              setPendingAllergyAction(null);
              return;
            }
          }

          setShowReview(true);
          setPendingAllergyAction(null);
        }}
      />

      <SyringeWarningDialog
        open={showSyringeWarning}
        onOpenChange={setShowSyringeWarning}
        injectableMedications={syringeWarningMedications}
        onGoBack={() => {
          setShowSyringeWarning(false);
          setPendingSyringeAction(null);
        }}
        onAcknowledge={() => {
          setSyringeWarningAcknowledged(true);
          setShowSyringeWarning(false);
          setShowReview(true);
          setPendingSyringeAction(null);
        }}
      />
    </TooltipProvider>
  );
};

export default PrescriptionFormSheet;
